// Shopify API integration for photo submission discount codes

import { Photo } from "../types/photos";
import { Settings } from "../components/dashboard/ShopifySettings";
import { supabase } from "@/lib/supabase";

// Hardcoded Shopify API configuration
const SHOPIFY_API_KEY = "9284140a0582506932d7348c265cbb95";
const SHOPIFY_API_SECRET = "bab55d4b5749f373fc6b3c6c11772f1c";
const SHOPIFY_ACCESS_TOKEN = "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";
const SHOPIFY_API_VERSION = "2023-10"; // Update this to the latest version as needed
const KLAVIYO_API_KEY = "pk_45b5effa8c79ecda4a9e308103d89e09f3";

// Safely get store URL from localStorage if available, otherwise use default
const getStoreUrl = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return "https://bainbridgeshop.myshopify.com/";
    }
    return "https://bainbridgeshop.myshopify.com/";
  } catch (error) {
    console.warn("localStorage not available, using default store URL");
    return "https://bainbridgeshop.myshopify.com/";
  }
};

const SHOPIFY_STORE_URL = getStoreUrl();

// Backend API endpoint for Shopify proxy - Updated with the provided backend URL
const BACKEND_API_URL = process.env.NODE_ENV === 'production'
  ? "https://photoharvestjsbackend.vercel.app"
  : "http://localhost:3000";

interface ShopifyDiscountCode {
  code: string;
  value: number;
  valueType: "percentage" | "fixed_amount";
  oneTimeUse: boolean;
}

interface ShopifySettings {
  shopify_store_url: string;
  shopify_api_key: string;
  shopify_api_secret: string;
  shopify_access_token: string;
}

interface ShopifyDiscountResponse {
  success: boolean;
  message: string;
  discountCode?: string;
}

/**
 * Returns the formatted Shopify store URL for API calls
 */
const getFormattedShopifyUrl = (): string => {
  let url = SHOPIFY_STORE_URL.trim();

  // Check if URL contains the protocol
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    url = "https://" + url;
  }

  // Remove trailing slash if present
  if (url.endsWith("/")) {
    url = url.slice(0, -1);
  }

  return url;
};

/**
 * Validates if the Shopify API credentials are configured
 */
export const isShopifyConfigured = (): boolean => {
  // Now we always have access token, so just check if store URL is provided
  return Boolean(SHOPIFY_STORE_URL && SHOPIFY_STORE_URL !== "your-store.myshopify.com");
};

/**
 * Configures the Shopify API credentials
 */

const LOCAL_STORAGE_KEYS_MAP = {
  storeName: "SHOPIFY_STORE_NAME",
  apiKey: "SHOPIFY_API_KEY",
  apiSecret: "SHOPIFY_API_SECRET",
  accessToken: "SHOPIFY_ACCESS_TOKEN",
  klaviyoApiKey: "KLAVIYO_API_KEY",
};

export const configureShopify = async (settings: Settings): Promise<void> => {
  console.log("Configuring Shopify with settings:", settings);

  try {
    // Get the current user's ID
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("No user found");
    }

    // Only update fields that are provided in settings
    const updateData: Record<string, string> = {};

    if (settings.storeUrl) {
      updateData.shopify_store_url = settings.storeUrl;
    }
    if (settings.apiKey) {
      updateData.shopify_api_key = settings.apiKey;
    }
    if (settings.apiSecret) {
      updateData.shopify_api_secret = settings.apiSecret;
    }
    if (settings.accessToken) {
      updateData.shopify_access_token = settings.accessToken;
    }
    if (settings.klaviyoApiKey) {
      updateData.klaviyo_api_key = settings.klaviyoApiKey;
    }

    // Only update if there are fields to update
    if (Object.keys(updateData).length > 0) {
      const { error } = await supabase
        .from("brands")
        .update(updateData)
        .eq("id", user.id);

      if (error) {
        throw error;
      }
    }
  } catch (error) {
    console.error("Error saving Shopify settings:", error);
    throw error;
  }
};

/**
 * Generates a one-time discount code
 * Now making actual API calls to the backend service
 */
export async function generateOneTimeDiscountCode(
  customer_email: string,
  accessToken: string,
  storeUrl: string
): Promise<{ code: string; amount: number }> {
  console.log("🛍️ Starting discount code generation...");

  // Get the current user's Klaviyo API key
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("No user found");
  }

  const { data: brandData } = await supabase
    .from("brands")
    .select("klaviyo_api_key")
    .eq("id", user.id)
    .single();

  // Use the exact same hardcoded values that worked in our test
  const defaultStoreUrl = "https://bainbridgeshop.myshopify.com";
  const defaultAccessToken = "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";
  const backendUrl = BACKEND_API_URL;

  const transformedStoreUrl = (storeUrl || defaultStoreUrl).replace("https://", "").replace(".myshopify.com", "");

  console.log("Using credentials:");
  console.log("Email:", customer_email);
  console.log("Store URL:", transformedStoreUrl);
  console.log("Access Token:", (accessToken || defaultAccessToken).substring(0, 5) + "...");
  console.log("Backend URL:", backendUrl);

  try {
    console.log("📤 Sending request to backend...");

    const response = await fetch(`${backendUrl}/api/discount-codes`, {
      method: "POST",
      headers: {
        "shopifystore": transformedStoreUrl,
        "access-token": accessToken || defaultAccessToken,
        "email": customer_email || "danwjones22@gmail.com",
        ...(brandData?.klaviyo_api_key && { "klaviyo-api-key": brandData.klaviyo_api_key })
      }
    });

    console.log("📥 Response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Error response:", errorText);
      throw new Error(`Error from backend: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log("✅ Raw response data:", data);

    // Check if we have a valid discount code in the response
    if (!data || !data.discountCode) {
      console.error("❌ Invalid response format:", data);
      throw new Error("Invalid response format from backend");
    }

    const result = {
      code: data.discountCode,
      amount: 25,
    };

    console.log("✨ Returning discount code:", result);
    return result;
  } catch (error) {
    console.error("❌ Error generating discount code:", error);
    throw error;
  }
}

/**
 * Setup Guide: How to properly integrate with Shopify
 *
 * 1. Create a Private App in your Shopify Admin
 *    - Go to Apps > Develop apps > Create an app
 *    - Set appropriate app name and permissions for:
 *      - Price rules: read_price_rules, write_price_rules
 *      - Discounts: read_discounts, write_discounts
 *      - Customers: read_customers, write_customers
 *
 * 2. Get your access credentials:
 *    - Admin API access token
 *    - Your store URL (yourstorename.myshopify.com)
 *
 * 3. Create a backend service (Node.js, serverless functions, etc.)
 *    - This service will make the actual API calls to Shopify
 *    - Store your Shopify API credentials securely on the backend
 *    - Implement endpoints for creating price rules and discount codes
 *
 * 4. Update the BACKEND_API_URL constant in this file
 *
 * 5. Update the Dashboard to use your backend service
 *
 * For local testing and development, this demo implementation
 * will generate mock discount codes without calling Shopify API.
 */

export const createShopifyDiscount = async (
  brandId: string,
  customerEmail: string,
  customerName: string
): Promise<ShopifyDiscountResponse> => {
  try {
    // First, get the brand's data from Supabase to get the required credentials
    const { data: brandData, error: brandError } = await supabase
      .from('brands')
      .select('shopify_access_token, klaviyo_api_key, shopify_store_url')
      .eq('id', brandId)
      .single();

    if (brandError || !brandData) {
      console.error('Error fetching brand data:', brandError);
      throw new Error('Could not find brand data');
    }

    // Validate required credentials
    if (!brandData.shopify_access_token || !brandData.klaviyo_api_key) {
      throw new Error('Missing required Shopify or Klaviyo credentials');
    }

    if (!customerEmail) {
      throw new Error('Customer email is required');
    }

    // Extract store name from store URL
    const storeUrl = brandData.shopify_store_url || 'https://bainbridgeshop.myshopify.com';
    const storeName = storeUrl
      .replace('https://', '')
      .replace('http://', '')
      .replace('.myshopify.com', '');

    console.log('Making discount code request with:', {
      storeName,
      customerEmail,
      hasAccessToken: !!brandData.shopify_access_token,
      hasKlaviyoKey: !!brandData.klaviyo_api_key
    });

    const response = await fetch('https://photoharvestjsbackend.vercel.app/api/discount-codes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'shopifystore': storeName,
        'access-token': brandData.shopify_access_token,
        'klaviyo-api-key': brandData.klaviyo_api_key,
        'email': customerEmail
      },
      mode: "cors"
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response from discount-codes endpoint:', errorText);
      throw new Error(`Failed to create discount: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Discount code created successfully:', data);

    return {
      success: true,
      message: 'Discount code created and email sent',
      discountCode: data.discountCode
    };
  } catch (error) {
    console.error('Error creating Shopify discount:', error);
    throw error;
  }
};
